import type { ReactNode } from "react";
import "./SegmentedControl.css";

/**
 * Claude 설정 창의 세그먼트 고르개.
 *
 * 값은 전부 `docs/rail-reference.json` 의 `themeSegment` 에서 온다 — claude.ai 설정 창의
 * `role="radiogroup"` 을 computed style 로 잰 것이고, 여기서 손으로 고치지 않는다.
 *
 * 칸들은 붙어 있고(사이 0) 묶음의 padding 1 이 양 끝 여백을 만든다. 고른 칸을 칠하는 것이
 * 아니라 **흰 썸 하나가 미끄러져** 그 자리에 선다 — 그래서 고르개가 몇 칸이든 움직임이 하나다.
 */
export type SegmentOption<T extends string> = { value: T; label: string; icon: ReactNode };

export function SegmentedControl<T extends string>({ value, options, onChange, ariaLabel }: {
  value: T;
  options: ReadonlyArray<SegmentOption<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
}) {
  const index = Math.max(0, options.findIndex(option => option.value === value));
  /* 뿌리를 div 가 아니라 span 으로 둔다 — 설정 줄의 `.account-settings-row > div` 규칙이
     세로 쌓기(column)를 걸어서 칸 셋이 겹쳤다. 특정도를 올려 이기는 것보다, 그 선택자에
     걸리지 않는 태그를 쓰는 편이 다음 사람이 읽기 쉽다. */
  return <span className="seg" role="radiogroup" aria-label={ariaLabel}>
    {/* 썸은 칸 뒤가 아니라 **아래**에 깔린다 — 칸이 글자·아이콘을 들고 있어야 색이 바뀐다. */}
    <span className="seg-thumb" aria-hidden="true" style={{ transform: `translateX(${index * 30}px)` }} />
    {options.map(option => <span key={option.value} role="radio" className="seg-option"
      aria-label={option.label} aria-checked={option.value === value} tabIndex={option.value === value ? 0 : -1}
      onClick={() => onChange(option.value)}
      onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onChange(option.value); } }}>
      {option.icon}
    </span>)}
  </span>;
}

/* 아이콘 셋. 20 상자에 굵기 1 — 잰 값 그대로다. */
const svg = (d: ReactNode) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>;
export const ICON_SYSTEM = svg(<><rect x="2.5" y="4" width="19" height="13" rx="2" /><path d="M8.5 21h7M12 17v4" /></>);
export const ICON_LIGHT = svg(<><circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2.4M12 19.1v2.4M4.22 4.22l1.7 1.7M18.08 18.08l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.22 19.78l1.7-1.7M18.08 5.92l1.7-1.7" /></>);
export const ICON_DARK = svg(<path d="M20.5 14.3A8.8 8.8 0 1 1 9.7 3.5a7 7 0 0 0 10.8 10.8Z" />);
